import {
  type SubmissionResult,
  getFormProps,
  useForm,
} from "@conform-to/react";
import { CloseButton } from "@headlessui/react";
import { Trans } from "@lingui/macro";
import { AlertActions } from "@winamp/ui/components/Alert/AlertActions.js";
import { AlertDescription } from "@winamp/ui/components/Alert/AlertDescription.js";
import { AlertTitle } from "@winamp/ui/components/Alert/AlertTitle.js";
import { AlertWrapper } from "@winamp/ui/components/Alert/AlertWrapper.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import type { Dialog } from "@winamp/ui/components/Dialog/Dialog.js";
import { Message } from "@winamp/ui/components/Message/Message.js";
import { MessageDescription } from "@winamp/ui/components/Message/MessageDescription.js";
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { useFetcher } from "react-router";
import { $path } from "safe-routes";
import type { ILink } from "~/models/link";
import type { action as DeleteArtistLinkAction } from "~/routes/resources+/artists.$artistId.links.$linkId";

type LinksLibraryDeleteDialogProps = {
  artistUid: string;
  link: ILink;
  children: ReactNode | (({ open }: { open: () => void }) => ReactNode);
  onComplete?: () => void;
} & Omit<
  ComponentProps<typeof Dialog>,
  "children" | "open" | "onClose" | "unmount"
>;

export function LinksLibraryDeleteDialog({
  artistUid,
  link,
  children,
  onComplete,
  ...props
}: LinksLibraryDeleteDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [lastResult, setLastResult] = useState<
    SubmissionResult<Array<string>> | null | undefined
  >(null);

  const formAction = $path("/resources/artists/:artistId/links/:linkId", {
    artistId: artistUid,
    linkId: link.uid,
  });
  const fetcher = useFetcher<typeof DeleteArtistLinkAction>({
    key: `${artistUid}-${link.uid}-deleteLink-action`,
  });
  const isPending = fetcher.state !== "idle";

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    setLastResult(fetcher.data);

    // Close after successful actions
    if (fetcher.data?.status === "success") {
      onComplete?.();
      handleClose();
    }
  }, [fetcher.data]);

  const [form] = useForm({
    id: [artistUid, link.uid, "deleteLink", isOpen].join("-"),
    lastResult,
  });

  return (
    <>
      {typeof children === "function"
        ? children({ open: () => setIsOpen(!isOpen) })
        : children}
      <fetcher.Form
        method="POST"
        action={formAction}
        {...getFormProps(form)}
        className="hidden"
      />
      <AlertWrapper
        {...props}
        open={isOpen}
        onClose={() => handleClose()}
        loading={isPending}
      >
        <AlertTitle>
          <Trans>Delete a Link</Trans>
        </AlertTitle>
        <AlertDescription>
          <Trans>
            Once a Link is deleted, it's also removed from where it was used
            like the Fanzone or the Winamp.link page.
          </Trans>
        </AlertDescription>
        <Message type="error" className="mt-3">
          <MessageDescription>
            <p>
              <Trans>Are you sure you want to delete this link?</Trans>
            </p>
            <p className="text-xs">
              {[link.label, link.url].filter(Boolean).join(" — ")}
            </p>
          </MessageDescription>
        </Message>
        <AlertActions>
          <CloseButton as={Button} variant="secondary">
            <Trans>Cancel</Trans>
          </CloseButton>
          <Button
            type="submit"
            variant="primary"
            color="red"
            form={form.id}
            name="action"
            value="delete"
            loading={isPending}
          >
            <Trans>Yes, Delete</Trans>
          </Button>
        </AlertActions>
      </AlertWrapper>
    </>
  );
}
